import { captureMessage } from '@sentry/nextjs'
import { z } from 'zod'

const EventSchema = z.object({
    name: z.string(),
    enabled: z.boolean(),
})

const ClaritySchema = z.object({
    enabled: z.boolean(),
    config: z.object({
        projectId: z.string(),
    }),
})
const GtmSchema = z.object({
    enabled: z.boolean(),
    config: z.object({
        containerId: z.string(),
    }),
    events: z.array(EventSchema),
})
const InternalSchema = z.object({
    enabled: z.boolean(),
    events: z.array(EventSchema),
})
const WebengageSchema = z.object({
    enabled: z.boolean(),
    config: z.object({
        identifierId: z.string().regex(/^[a-zA-Z0-9]+$/, 'identifierId must be alphanumeric'),
        isGzip: z.boolean(),
        proxy: z.discriminatedUnion('enabled', [
            z.object({
                enabled: z.literal(true),
                url: z.string().url(),
            }),
            z.object({ enabled: z.literal(false) }),
        ]),
    }),
    events: z.array(EventSchema),
})
const GoftinoSchema = z.object({
    enabled: z.boolean(),
})
const YektanetSchema = z.object({
    enabled: z.boolean(),
})
const MediaadSchema = z.object({
    enabled: z.boolean(),
})
const DezhinoSchema = z.object({
    enabled: z.boolean(),
})
const DeemaSchema = z.object({
    enabled: z.boolean(),
    config: z.object({
        scriptUrl: z.string().url().nullable(),
    }),
})
const BinobanSchema = z.object({
    enabled: z.boolean(),
    config: z.object({
        sourceIdentifier: z.string(),
        apiKey: z.string(),
        specificUrl: z.string().url(),
    }),
})

export type SingleEvent = z.infer<typeof EventSchema>

const ProviderSchemas = {
    clarity: ClaritySchema,
    gtm: GtmSchema,
    internal: InternalSchema,
    webengage: WebengageSchema,
    goftino: GoftinoSchema,
    yektanet: YektanetSchema,
    mediaad: MediaadSchema,
    dezhino: DezhinoSchema,
    deema: DeemaSchema,
    binoban: BinobanSchema,
} satisfies Record<string, z.ZodTypeAny>

type ProviderKey = keyof typeof ProviderSchemas
export type ValidatedScripts = {
    [K in ProviderKey]?: z.infer<(typeof ProviderSchemas)[K]>
}

export function validateThirdPartyScripts(raw: unknown): ValidatedScripts {
    if (typeof raw !== 'object' || raw === null) return {}

    const result: ValidatedScripts = {}
    const source = raw as Record<string, unknown>

    for (const key of Object.keys(ProviderSchemas) as ProviderKey[]) {
        const providerData = source[key]
        if (providerData === undefined) {
            captureMessage(`[third-party-scripts] config missing for: "${key}"`, {
                tags: {
                    feature: 'third-party-scripts',
                    reason: 'schema_validation_failed',
                    sampleRate: '1.0',
                },
            })
            continue
        }

        const parsed = ProviderSchemas[key].safeParse(providerData)

        if (!parsed.success) {
            captureMessage(`[third-party-scripts] invalid config for "${key}"`, {
                tags: {
                    feature: 'third-party-scripts',
                    reason: 'schema_validation_failed',
                    sampleRate: '1.0',
                },
                extra: {
                    zodErrors: parsed.error.flatten(),
                },
            })

            continue
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result[key] = parsed.data as any
    }

    return result
}
